import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ClipboardList, Paperclip, Eye, Pencil, Trash2, Plus, CheckCircle, XCircle, Search, BarChart3, RefreshCw, User, MousePointerClick } from 'lucide-react';
import SiretLookup from './SiretLookup';
import './LeadsManagement.css';

import { useRafraichissementAuto } from '../rafraichissementAuto';
function LeadsManagement({ currentUser }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [filterStatut, setFilterStatut] = useState('tous');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [users, setUsers] = useState([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [previousLeads, setPreviousLeads] = useState([]);
  const [loadingPreviousLeads, setLoadingPreviousLeads] = useState(false);
  const [clickStats, setClickStats] = useState({});
  const [showClickDetail, setShowClickDetail] = useState(false);
  const [clickDetailData, setClickDetailData] = useState([]);
  const [clickDetailLabel, setClickDetailLabel] = useState('');
  const [loadingClickDetail, setLoadingClickDetail] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    entreprise: '',
    site_web: '',
    message: '',
    source: 'manuel',
    notes: ''
  });

  const API_URL = process.env.REACT_APP_API_URL || '';

  // Mise a jour automatique de la page (voir src/rafraichissementAuto.js)
  useRafraichissementAuto(() => { fetchLeads(true); fetchStats() }, { secondes: 30 });

  useEffect(() => {
    fetchLeads();
    fetchStats();
    if (currentUser?.role === 'admin') fetchUsers();
    if (currentUser?.role === 'admin') fetchClickStats();
  }, [filterStatut, searchTerm]);

  const fetchClickStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/link-clicks/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClickStats(response.data);
    } catch (error) {
      console.error('Erreur chargement stats clics:', error);
    }
  };

  const openClickDetail = async (linkName, label) => {
    setClickDetailLabel(label);
    setLoadingClickDetail(true);
    setShowClickDetail(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/link-clicks/detail/${linkName}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClickDetailData(response.data);
    } catch (error) {
      console.error('Erreur chargement detail:', error);
      setClickDetailData([]);
    } finally {
      setLoadingClickDetail(false);
    }
  };

  const parseBrowser = (ua) => {
    if (!ua) return 'Inconnu';
    if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
    if (ua.includes('Edg')) return 'Edge';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
    if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
    return 'Autre';
  };

  const parseDevice = (ua) => {
    if (!ua) return 'Inconnu';
    if (ua.includes('iPhone') || ua.includes('Android')) return 'Mobile';
    if (ua.includes('iPad') || ua.includes('Tablet')) return 'Tablette';
    return 'Desktop';
  };

  const fetchLeads = async (silencieux = false) => {
    try {
      if (!silencieux) setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterStatut !== 'tous') params.append('statut', filterStatut);
      if (searchTerm) params.append('search', searchTerm);

      const response = await axios.get(`${API_URL}/api/leads?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data);
    } catch (error) {
      console.error('Erreur chargement leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/leads/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.filter(u => u.role === 'agent' || u.role === 'admin'));
    } catch (error) {
      console.error('Erreur chargement utilisateurs:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (editingLead) {
        await axios.put(`${API_URL}/api/leads/${editingLead.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_URL}/api/leads`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setShowModal(false);
      setEditingLead(null);
      setFormData({
        nom: '', email: '', telephone: '', entreprise: '',
        site_web: '', message: '', source: 'manuel', notes: ''
      });
      fetchLeads();
      fetchStats();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (lead) => {
    setEditingLead(lead);
    setFormData({
      nom: lead.nom,
      email: lead.email,
      telephone: lead.telephone || '',
      entreprise: lead.entreprise || '',
      site_web: lead.site_web || '',
      message: lead.message || '',
      source: lead.source || 'manuel',
      notes: lead.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce lead ?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/leads/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchLeads();
      fetchStats();
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const handleChangeStatut = async (id, statut) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/api/leads/${id}/statut`, { statut }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchLeads();
      fetchStats();
    } catch (error) {
      alert('Erreur lors du changement de statut');
    }
  };

  const handleAssigner = async (id, agent_id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/api/leads/${id}/assigner`, { agent_id: agent_id || null }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchLeads();
    } catch (error) {
      alert('Erreur lors de l\'assignation');
    }
  };

  const handleConvertir = async (lead) => {
    if (!window.confirm(`Convertir "${lead.nom}" en client ?`)) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/leads/${lead.id}/convertir`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`Lead converti ! Client n${response.data.client.numero_client} cree.`);
      fetchLeads();
      fetchStats();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de la conversion');
    }
  };

  const handleViewDetail = async (lead) => {
    setSelectedLead(lead);
    setShowDetailModal(true);
    setPreviousLeads([]);

    // Fetch all leads with the same email to show history
    try {
      setLoadingPreviousLeads(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/leads?search=${encodeURIComponent(lead.email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to get only leads with exact same email, excluding current lead
      const relatedLeads = response.data.filter(l =>
        l.email.toLowerCase() === lead.email.toLowerCase() && l.id !== lead.id
      );
      setPreviousLeads(relatedLeads);
    } catch (error) {
      console.error('Erreur chargement historique:', error);
    } finally {
      setLoadingPreviousLeads(false);
    }
  };

  const getStatutBadge = (statut) => {
    const badges = {
      nouveau: { bg: '#e3f2fd', color: '#1565c0', label: 'Nouveau' },
      contacte: { bg: '#fff3e0', color: '#ef6c00', label: 'Contacte' },
      interesse: { bg: '#e1bee7', color: '#7b1fa2', label: 'Interesse' },
      rdv_pris: { bg: '#b3e5fc', color: '#0277bd', label: 'RDV Pris' },
      negociation: { bg: '#ffe0b2', color: '#e65100', label: 'Negociation' },
      qualifie: { bg: '#e8f5e9', color: '#2e7d32', label: 'Qualifie' },
      converti: { bg: '#c8e6c9', color: '#1b5e20', label: 'Converti' },
      perdu: { bg: '#ffebee', color: '#c62828', label: 'Perdu' },
      faux_num: { bg: '#fce4ec', color: '#880e4f', label: 'Faux Num' }
    };
    const badge = badges[statut] || badges.nouveau;
    return (
      <span style={{
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        backgroundColor: badge.bg,
        color: badge.color
      }}>
        {badge.label}
      </span>
    );
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const copyAuditLink = () => {
    const url = `${window.location.origin}/audit-gratuit`;
    navigator.clipboard.writeText(url);
    alert('Lien copie dans le presse-papiers !');
  };

  const copyBorniiLink = () => {
    navigator.clipboard.writeText('https://e-application.fr/bornii');
    alert('Lien Bornii copie dans le presse-papiers !');
  };

  const copyCeeLink = () => {
    const url = `${window.location.origin}/leads-cee`;
    navigator.clipboard.writeText(url);
    alert('Lien CEE Leads copie dans le presse-papiers !');
  };

  const copyAmStramGramLink = () => {
    navigator.clipboard.writeText('https://e-application.fr/boutique-influenceurs');
    alert('Lien Am Stram Gram copie dans le presse-papiers !');
  };

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <div>
          <h2 style={{ margin: 0, color: '#333', fontSize: '24px' }}>Leads</h2>
          <p style={{ margin: '5px 0 0', color: '#666', fontSize: '14px' }}>
            Prospects issus des formulaires d'audit gratuit
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={copyAuditLink}
            style={{
              padding: '10px 18px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            Copier lien Audit
          </button>
          <button
            onClick={copyBorniiLink}
            style={{
              padding: '10px 18px',
              backgroundColor: '#e94560',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            Copier lien Bornii
          </button>
          <button
            onClick={copyCeeLink}
            style={{
              padding: '10px 18px',
              backgroundColor: '#00cc88',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            Copier lien CEE
          </button>
          <button
            onClick={copyAmStramGramLink}
            style={{
              padding: '10px 18px',
              backgroundColor: '#d4af37',
              color: '#1a1407',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            Copier lien Am Stram Gram
          </button>
          <button
            onClick={() => { setEditingLead(null); setFormData({ nom: '', email: '', telephone: '', entreprise: '', site_web: '', message: '', source: 'manuel', notes: '' }); setShowModal(true); }}
            style={{
              padding: '10px 18px',
              backgroundColor: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            + Ajouter un lead
          </button>
        </div>
      </div>

      {/* Visites des pages publiques */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {[
          { key: 'audit-gratuit', label: 'Page Audit Gratuit', color: '#6c757d' },
          { key: 'bornii', label: 'Page Bornii', color: '#e94560' },
          { key: 'leads-cee', label: 'Page CEE Leads', color: '#00cc88' },
          { key: 'boutique-influenceurs', label: 'Page Am Stram Gram', color: '#d4af37' }
        ].map(link => {
          const s = clickStats[link.key] || { total: 0, aujourd_hui: 0, semaine: 0, mois: 0 };
          return (
            <div key={link.key} style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '10px 16px', borderRadius: '10px',
              background: '#fff', border: `2px solid ${link.color}20`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              minWidth: '200px'
            }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '8px',
                background: `${link.color}15`, display: 'flex',
                alignItems: 'center', justifyContent: 'center'
              }}>
                <MousePointerClick size={18} style={{ color: link.color }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', color: '#888', fontWeight: '600', marginBottom: '2px' }}>{link.label}</div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'baseline' }}>
                  <span style={{ fontSize: '20px', fontWeight: '800', color: link.color }}>{s.total}</span>
                  <span style={{ fontSize: '10px', color: '#aaa' }}>visites</span>
                </div>
                <div style={{ display: 'flex', gap: '6px', marginTop: '3px', alignItems: 'center' }}>
                  <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '4px', background: '#f0f0f0', color: '#666' }}>
                    Jour: {s.aujourd_hui}
                  </span>
                  <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '4px', background: '#f0f0f0', color: '#666' }}>
                    Sem: {s.semaine}
                  </span>
                  <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '4px', background: '#f0f0f0', color: '#666' }}>
                    Mois: {s.mois}
                  </span>
                  <button
                    onClick={() => openClickDetail(link.key, link.label)}
                    style={{
                      fontSize: '10px', padding: '2px 8px', borderRadius: '4px',
                      background: link.color, color: '#fff', border: 'none',
                      cursor: 'pointer', fontWeight: '600', marginLeft: 'auto'
                    }}
                  >
                    Voir detail
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px', marginBottom: '25px' }}>
          <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '16px', borderRadius: '12px', color: 'white' }}>
            <div style={{ fontSize: '24px', fontWeight: '700' }}>{stats.total}</div>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>Total</div>
          </div>
          <div style={{ background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)', padding: '16px', borderRadius: '12px', color: 'white' }}>
            <div style={{ fontSize: '24px', fontWeight: '700' }}>{stats.nouveaux}</div>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>Nouveaux</div>
          </div>
          <div style={{ background: 'linear-gradient(135deg, #ef6c00 0%, #f57c00 100%)', padding: '16px', borderRadius: '12px', color: 'white' }}>
            <div style={{ fontSize: '24px', fontWeight: '700' }}>{stats.contactes}</div>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>Contactes</div>
          </div>
          <div style={{ background: 'linear-gradient(135deg, #7b1fa2 0%, #9c27b0 100%)', padding: '16px', borderRadius: '12px', color: 'white' }}>
            <div style={{ fontSize: '24px', fontWeight: '700' }}>{stats.interesses || 0}</div>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>Interesses</div>
          </div>
          <div style={{ background: 'linear-gradient(135deg, #0277bd 0%, #0288d1 100%)', padding: '16px', borderRadius: '12px', color: 'white' }}>
            <div style={{ fontSize: '24px', fontWeight: '700' }}>{stats.rdvPris || 0}</div>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>RDV Pris</div>
          </div>
          <div style={{ background: 'linear-gradient(135deg, #e65100 0%, #ef6c00 100%)', padding: '16px', borderRadius: '12px', color: 'white' }}>
            <div style={{ fontSize: '24px', fontWeight: '700' }}>{stats.negociations || 0}</div>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>Negociation</div>
          </div>
          <div style={{ background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)', padding: '16px', borderRadius: '12px', color: 'white' }}>
            <div style={{ fontSize: '24px', fontWeight: '700' }}>{stats.convertis || 0}</div>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>Convertis</div>
          </div>
          <div style={{ background: 'linear-gradient(135deg, #1b5e20 0%, #388e3c 100%)', padding: '16px', borderRadius: '12px', color: 'white' }}>
            <div style={{ fontSize: '24px', fontWeight: '700' }}>{stats.tauxConversion}%</div>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>Conversion</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Rechercher..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: '10px 15px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            fontSize: '14px',
            minWidth: '250px'
          }}
        />
        <select
          value={filterStatut}
          onChange={(e) => setFilterStatut(e.target.value)}
          style={{
            padding: '10px 15px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            fontSize: '14px',
            backgroundColor: 'white'
          }}
        >
          <option value="tous">Tous les statuts</option>
          <option value="nouveau">Nouveau</option>
          <option value="contacte">Contacte</option>
          <option value="interesse">Interesse</option>
          <option value="rdv_pris">RDV Pris</option>
          <option value="negociation">Negociation</option>
          <option value="qualifie">Qualifie</option>
          <option value="converti">Converti</option>
          <option value="perdu">Perdu</option>
        </select>
      </div>

      {/* Leads Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Chargement...</div>
      ) : leads.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666', backgroundColor: '#f8f9fa', borderRadius: '12px' }}>
          <div style={{ fontSize: '48px', marginBottom: '15px' }}><ClipboardList size={48} /></div>
          <p>Aucun lead trouve</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '14px', textAlign: 'center', fontWeight: '600', color: '#333', borderBottom: '2px solid #eee', width: '50px' }}></th>
                <th style={{ padding: '14px', textAlign: 'left', fontWeight: '600', color: '#333', borderBottom: '2px solid #eee' }}>Statut</th>
                <th style={{ padding: '14px', textAlign: 'left', fontWeight: '600', color: '#333', borderBottom: '2px solid #eee' }}>Date</th>
                <th style={{ padding: '14px', textAlign: 'left', fontWeight: '600', color: '#333', borderBottom: '2px solid #eee' }}>Contact</th>
                <th style={{ padding: '14px', textAlign: 'left', fontWeight: '600', color: '#333', borderBottom: '2px solid #eee' }}>Entreprise</th>
                <th style={{ padding: '14px', textAlign: 'left', fontWeight: '600', color: '#333', borderBottom: '2px solid #eee' }}>Source</th>
                <th style={{ padding: '14px', textAlign: 'left', fontWeight: '600', color: '#333', borderBottom: '2px solid #eee' }}>Fichier</th>
                <th style={{ padding: '14px', textAlign: 'left', fontWeight: '600', color: '#333', borderBottom: '2px solid #eee' }}>Agent</th>
                <th style={{ padding: '14px', textAlign: 'center', fontWeight: '600', color: '#333', borderBottom: '2px solid #eee' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleViewDetail(lead)}
                      title="Voir details"
                      style={{ padding: '6px 12px', backgroundColor: '#667eea', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '500' }}
                    >
                      Voir
                    </button>
                  </td>
                  <td style={{ padding: '14px' }}>
                    <select
                      value={lead.statut}
                      onChange={(e) => handleChangeStatut(lead.id, e.target.value)}
                      style={{
                        padding: '6px 10px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '12px',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="nouveau">NOUVEAU</option>
                      <option value="nrp">NRP</option>
                      <option value="pas_interesse">PAS INTERESSE</option>
                      <option value="rappel">RAPPEL</option>
                      <option value="rdv_pris">RDV PRIS</option>
                      <option value="faux_num">FAUX NUM</option>
                    </select>
                  </td>
                  <td style={{ padding: '14px', fontSize: '13px', color: '#666' }}>
                    {formatDate(lead.created_at)}
                    {lead.statut === 'nouveau' && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#ff4444', color: 'white', padding: '2px 6px', borderRadius: '10px', fontSize: '10px', fontWeight: '600' }}>NEW</span>
                    )}
                  </td>
                  <td style={{ padding: '14px' }}>
                    <div style={{ fontWeight: '600', color: '#333' }}>
                      {lead.nom}
                      {lead.message && lead.message.includes('DOUBLON') && (
                        <span style={{ marginLeft: '8px', backgroundColor: '#ff9800', color: 'white', padding: '2px 6px', borderRadius: '10px', fontSize: '10px', fontWeight: '600' }}>DOUBLON</span>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{lead.email}</div>
                    {lead.telephone && <div style={{ fontSize: '12px', color: '#888' }}>{lead.telephone}</div>}
                  </td>
                  <td style={{ padding: '14px' }}>
                    <div style={{ fontWeight: '500', color: '#333' }}>{lead.entreprise || '-'}</div>
                    {lead.site_web && (
                      <a href={lead.site_web.startsWith('http') ? lead.site_web : `https://${lead.site_web}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#667eea' }}>
                        {lead.site_web}
                      </a>
                    )}
                  </td>
                  <td style={{ padding: '14px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>
                      {lead.source === 'audit-gratuit' ? 'Audit Gratuit' :
                       lead.source === 'bornii' ? 'Bornii' :
                       lead.source === 'E-Shops' ? 'E-Shops' :
                       lead.source === 'QRcode' ? 'QR Code' :
                       lead.source === 'marketing-site' ? 'Marketing' :
                       lead.source === 'cee-demo-gratuite' ? 'CEE Demo' :
                       lead.source === 'cee-abonnement-189' ? 'CEE Abo 189€' :
                       lead.source === 'cee-leads-b2b' ? 'CEE Leads' :
                       lead.source === 'manuel' ? 'Manuel' :
                       lead.source || 'Inconnu'}
                    </div>
                    {lead.source_url && (
                      <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }} title={lead.source_url}>
                        {lead.source_url.replace(/https?:\/\//, '').split('?')[0]}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '14px' }}>
                    {lead.fichier_url ? (
                      <a
                        href={`${API_URL}${lead.fichier_url}`}
                        download={lead.fichier_nom || 'fichier'}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 8px',
                          backgroundColor: '#e8f5e9',
                          color: '#2e7d32',
                          borderRadius: '6px',
                          fontSize: '11px',
                          textDecoration: 'none',
                          fontWeight: '500'
                        }}
                      >
                        <Paperclip size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> {lead.fichier_nom ? lead.fichier_nom.substring(0, 15) + (lead.fichier_nom.length > 15 ? '...' : '') : 'Fichier'}
                      </a>
                    ) : (
                      <span style={{ color: '#999', fontSize: '12px' }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '14px' }}>
                    {currentUser?.role === 'admin' ? (
                      <select
                        value={lead.agent_id || ''}
                        onChange={(e) => handleAssigner(lead.id, e.target.value)}
                        style={{
                          padding: '6px 10px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '12px',
                          backgroundColor: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">Non assigne</option>
                        {users.map(u => (
                          <option key={u.id} value={u.id}>{u.nom}</option>
                        ))}
                      </select>
                    ) : (
                      <span style={{ fontSize: '13px', color: '#666' }}>{lead.agent_nom || 'Non assigne'}</span>
                    )}
                  </td>
                  <td style={{ padding: '14px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                      <button
                        onClick={() => handleEdit(lead)}
                        title="Modifier"
                        style={{ padding: '6px 10px', backgroundColor: '#fff3e0', color: '#ef6c00', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
                      >
                        Editer
                      </button>
                      {lead.statut !== 'converti' && (
                        <button
                          onClick={() => handleConvertir(lead)}
                          title="Convertir en client"
                          style={{ padding: '6px 10px', backgroundColor: '#e8f5e9', color: '#2e7d32', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
                        >
                          Convertir
                        </button>
                      )}
                      {currentUser && currentUser.role === 'admin' && (
                      <button
                        onClick={() => handleDelete(lead.id)}
                        title="Supprimer"
                        style={{ padding: '6px 10px', backgroundColor: '#ffebee', color: '#c62828', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
                      >
                        X
                      </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Ajout/Edition */}
      {showModal && (
        <div className="leads-modal-overlay">
          <div className="leads-modal-content leads-modal-form">
            <div className="modal-header">
              <h3>{editingLead ? 'Modifier le lead' : 'Ajouter un lead'}</h3>
              <button className="btn-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div className="leads-modal-body">
              <form onSubmit={handleSubmit}>
                <SiretLookup
                  compact
                  onCompanyFound={(data) => {
                    setFormData(prev => ({
                      ...prev,
                      nom: data.dirigeant_nom || prev.nom,
                      entreprise: data.entreprise || prev.entreprise
                    }));
                  }}
                />
                <div className="form-group">
                  <label>Nom *</label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Telephone</label>
                  <input
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Entreprise</label>
                  <input
                    type="text"
                    value={formData.entreprise}
                    onChange={(e) => setFormData({ ...formData, entreprise: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Site web</label>
                  <input
                    type="text"
                    value={formData.site_web}
                    onChange={(e) => setFormData({ ...formData, site_web: e.target.value })}
                    placeholder="www.exemple.com"
                  />
                </div>
                <div className="form-group">
                  <label>Message / Besoins</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="form-group">
                  <label>Notes internes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={2}
                  />
                </div>
                <div className="leads-modal-footer" style={{ padding: 0, border: 'none', marginTop: '20px' }}>
                  <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                    Annuler
                  </button>
                  <button type="submit" className="btn-save">
                    {editingLead ? 'Modifier' : 'Ajouter'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detail */}
      {showDetailModal && selectedLead && (
        <div className="leads-modal-overlay">
          <div className="leads-modal-content leads-modal-detail">
            <div className="modal-header">
              <h3>Details du Lead</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {getStatutBadge(selectedLead.statut)}
                <button className="btn-close" onClick={() => setShowDetailModal(false)}>&times;</button>
              </div>
            </div>
            <div className="leads-modal-body">

            <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '12px', marginBottom: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Nom</div>
                  <div style={{ fontWeight: '600', color: '#333' }}>{selectedLead.nom}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Email</div>
                  <div style={{ color: '#333' }}>{selectedLead.email}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Telephone</div>
                  <div style={{ color: '#333' }}>{selectedLead.telephone || '-'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Entreprise</div>
                  <div style={{ color: '#333' }}>{selectedLead.entreprise || '-'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Site web</div>
                  <div style={{ color: '#333' }}>
                    {selectedLead.site_web ? (
                      <a href={selectedLead.site_web.startsWith('http') ? selectedLead.site_web : `https://${selectedLead.site_web}`} target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>
                        {selectedLead.site_web}
                      </a>
                    ) : '-'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Source</div>
                  <div style={{ color: '#333' }}>{selectedLead.source || 'audit-gratuit'}</div>
                  {selectedLead.source_url && (
                    <div style={{ fontSize: '11px', color: '#888', marginTop: '4px', wordBreak: 'break-all' }}>{selectedLead.source_url}</div>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Agent assigne</div>
                  <div style={{ color: '#333' }}>{selectedLead.agent_nom || 'Non assigne'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Date creation</div>
                  <div style={{ color: '#333' }}>{formatDate(selectedLead.created_at)}</div>
                </div>
              </div>
            </div>

            {selectedLead.fichier_url && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px', fontWeight: '600' }}>Fichier joint</div>
                <a
                  href={`${API_URL}${selectedLead.fichier_url}`}
                  download={selectedLead.fichier_nom || 'fichier'}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    backgroundColor: '#e8f5e9',
                    color: '#2e7d32',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: '500'
                  }}
                >
                  <Paperclip size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Telecharger: {selectedLead.fichier_nom || 'Fichier'}
                </a>
              </div>
            )}

            {selectedLead.message && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px', fontWeight: '600' }}>Message / Besoins</div>
                <div style={{
                  backgroundColor: selectedLead.message.includes('DOUBLON') ? '#fff3e0' : '#e3f2fd',
                  padding: '15px',
                  borderRadius: '8px',
                  color: '#333',
                  whiteSpace: 'pre-wrap',
                  border: selectedLead.message.includes('DOUBLON') ? '2px solid #ff9800' : 'none'
                }}>
                  {selectedLead.message}
                </div>
              </div>
            )}

            {selectedLead.notes && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px', fontWeight: '600' }}>Notes internes</div>
                <div style={{ backgroundColor: '#fff3e0', padding: '15px', borderRadius: '8px', color: '#333', whiteSpace: 'pre-wrap' }}>
                  {selectedLead.notes}
                </div>
              </div>
            )}

            {/* Section Historique des demandes précédentes */}
            {loadingPreviousLeads ? (
              <div style={{ marginBottom: '20px', textAlign: 'center', color: '#666' }}>
                Chargement de l'historique...
              </div>
            ) : previousLeads.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{
                  fontSize: '14px',
                  color: '#333',
                  marginBottom: '12px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{
                    backgroundColor: '#ff9800',
                    color: 'white',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '12px'
                  }}>
                    {previousLeads.length} demande{previousLeads.length > 1 ? 's' : ''} précédente{previousLeads.length > 1 ? 's' : ''}
                  </span>
                  Historique pour {selectedLead.email}
                </div>
                <div style={{
                  backgroundColor: '#fff8e1',
                  border: '2px solid #ff9800',
                  borderRadius: '12px',
                  overflow: 'hidden'
                }}>
                  {previousLeads.map((prevLead, index) => (
                    <div
                      key={prevLead.id}
                      style={{
                        padding: '15px',
                        borderBottom: index < previousLeads.length - 1 ? '1px solid #ffe0b2' : 'none',
                        backgroundColor: index % 2 === 0 ? '#fff8e1' : '#fffde7'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <span style={{
                            padding: '3px 8px',
                            backgroundColor:
                              prevLead.source === 'audit-gratuit' ? '#e3f2fd' :
                              prevLead.source === 'bornii' ? '#fce4ec' :
                              prevLead.source === 'E-Shops' ? '#e8f5e9' :
                              prevLead.source === 'QRcode' ? '#fff8e1' :
                              prevLead.source === 'marketing-site' ? '#fff3e0' :
                              prevLead.source?.startsWith('cee-') ? '#e0f2f1' :
                              prevLead.source === 'manuel' ? '#f3e5f5' :
                              '#e0e0e0',
                            color:
                              prevLead.source === 'audit-gratuit' ? '#1565c0' :
                              prevLead.source === 'bornii' ? '#c2185b' :
                              prevLead.source === 'E-Shops' ? '#2e7d32' :
                              prevLead.source === 'QRcode' ? '#f57f17' :
                              prevLead.source === 'marketing-site' ? '#e65100' :
                              prevLead.source?.startsWith('cee-') ? '#00695c' :
                              prevLead.source === 'manuel' ? '#7b1fa2' :
                              '#424242',
                            borderRadius: '8px',
                            fontSize: '10px',
                            fontWeight: '600'
                          }}>
                            {prevLead.source === 'audit-gratuit' ? 'Audit Gratuit' :
                             prevLead.source === 'bornii' ? 'Bornii' :
                             prevLead.source === 'E-Shops' ? 'E-Shops' :
                             prevLead.source === 'QRcode' ? 'QR Code' :
                             prevLead.source === 'marketing-site' ? 'Marketing' :
                             prevLead.source === 'cee-demo-gratuite' ? 'CEE Demo' :
                             prevLead.source === 'cee-abonnement-189' ? 'CEE Abo 189€' :
                             prevLead.source === 'cee-leads-b2b' ? 'CEE Leads' :
                             prevLead.source === 'manuel' ? 'Manuel' :
                             prevLead.source || 'Inconnu'}
                          </span>
                          {getStatutBadge(prevLead.statut)}
                        </div>
                        <span style={{ fontSize: '11px', color: '#666' }}>{formatDate(prevLead.created_at)}</span>
                      </div>
                      {prevLead.message && (
                        <div style={{
                          fontSize: '13px',
                          color: '#555',
                          backgroundColor: 'white',
                          padding: '10px',
                          borderRadius: '6px',
                          marginTop: '8px',
                          whiteSpace: 'pre-wrap',
                          maxHeight: '100px',
                          overflowY: 'auto'
                        }}>
                          {prevLead.message}
                        </div>
                      )}
                      {prevLead.notes && (
                        <div style={{
                          fontSize: '12px',
                          color: '#7b1fa2',
                          fontStyle: 'italic',
                          marginTop: '6px',
                          padding: '6px 10px',
                          backgroundColor: '#f3e5f5',
                          borderRadius: '6px'
                        }}>
                          Note: {prevLead.notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            </div>
            <div className="leads-modal-footer">
              <button className="btn-cancel" onClick={() => setShowDetailModal(false)}>
                Fermer
              </button>
              <button className="btn-save" onClick={() => { setShowDetailModal(false); handleEdit(selectedLead); }}>
                Modifier
              </button>
              {selectedLead.statut !== 'converti' && (
                <button className="btn-convert" onClick={() => { setShowDetailModal(false); handleConvertir(selectedLead); }}>
                  Convertir en client
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Detail Visites */}
      {showClickDetail && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff', borderRadius: '16px', width: '95%', maxWidth: '950px',
            maxHeight: '85vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '20px 25px', borderBottom: '1px solid #eee'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', color: '#333' }}>
                  Visites - {clickDetailLabel}
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#888' }}>
                  {clickDetailData.length} visite{clickDetailData.length > 1 ? 's' : ''} enregistree{clickDetailData.length > 1 ? 's' : ''}
                </p>
              </div>
              <button onClick={() => setShowClickDetail(false)} style={{
                width: '32px', height: '32px', borderRadius: '8px', border: 'none',
                background: '#f5f5f5', cursor: 'pointer', fontSize: '18px',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>X</button>
            </div>

            {/* Content */}
            <div style={{ overflow: 'auto', flex: 1, padding: '0' }}>
              {loadingClickDetail ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>
                  <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
                  <p>Chargement...</p>
                </div>
              ) : clickDetailData.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>
                  Aucune visite enregistree
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa', position: 'sticky', top: 0 }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#555', borderBottom: '2px solid #eee' }}>Date & Heure</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#555', borderBottom: '2px solid #eee' }}>IP</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#555', borderBottom: '2px solid #eee' }}>Pays / Ville</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#555', borderBottom: '2px solid #eee' }}>Navigateur</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#555', borderBottom: '2px solid #eee' }}>Appareil</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#555', borderBottom: '2px solid #eee' }}>Ecran</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#555', borderBottom: '2px solid #eee' }}>Langue</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#555', borderBottom: '2px solid #eee' }}>Referer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clickDetailData.map((visit, idx) => (
                      <tr key={visit.id} style={{ background: idx % 2 === 0 ? '#fff' : '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                          <div style={{ fontWeight: '600', color: '#333' }}>
                            {new Date(visit.clicked_at).toLocaleDateString('fr-FR')}
                          </div>
                          <div style={{ color: '#888', fontSize: '11px' }}>
                            {new Date(visit.clicked_at).toLocaleTimeString('fr-FR')}
                          </div>
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <span style={{ fontFamily: 'monospace', fontSize: '11px', background: '#f0f0f0', padding: '2px 6px', borderRadius: '4px' }}>
                            {visit.ip_address === '77.137.66.124' ? 'Administration' : (visit.ip_address || '-')}
                          </span>
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <div style={{ fontWeight: '500', color: '#333' }}>{visit.country || '-'}</div>
                          {visit.city && <div style={{ fontSize: '11px', color: '#888' }}>{visit.city}</div>}
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <span style={{
                            padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                            background: parseBrowser(visit.user_agent) === 'Chrome' ? '#e8f5e9' :
                                       parseBrowser(visit.user_agent) === 'Safari' ? '#e3f2fd' :
                                       parseBrowser(visit.user_agent) === 'Firefox' ? '#fff3e0' :
                                       '#f5f5f5',
                            color: parseBrowser(visit.user_agent) === 'Chrome' ? '#2e7d32' :
                                   parseBrowser(visit.user_agent) === 'Safari' ? '#1565c0' :
                                   parseBrowser(visit.user_agent) === 'Firefox' ? '#e65100' :
                                   '#666'
                          }}>
                            {parseBrowser(visit.user_agent)}
                          </span>
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <span style={{
                            padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                            background: parseDevice(visit.user_agent) === 'Mobile' ? '#fce4ec' :
                                       parseDevice(visit.user_agent) === 'Tablette' ? '#f3e5f5' :
                                       '#e8eaf6',
                            color: parseDevice(visit.user_agent) === 'Mobile' ? '#c2185b' :
                                   parseDevice(visit.user_agent) === 'Tablette' ? '#7b1fa2' :
                                   '#283593'
                          }}>
                            {parseDevice(visit.user_agent)}
                          </span>
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: '11px', color: '#666' }}>
                          {visit.screen_width && visit.screen_height ? `${visit.screen_width}x${visit.screen_height}` : '-'}
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: '11px', color: '#666' }}>
                          {visit.language || '-'}
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: '11px', color: '#888', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={visit.referer || ''}>
                          {visit.referer ? visit.referer.replace(/https?:\/\//, '').split('?')[0] : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeadsManagement;
